import { TYPES } from "@/types";
import { myContainer } from "@/inversify.config";
import { IEmploymentService } from "@/interfaces/IEmploymentService";
import * as dtos from "@/models/dtos";
import { ContainerResponse, MetaData } from "../../models/dtos";
import { MainContainer } from "../../models/MainContainer";
import { ToastNotification } from "@/models/ToastNotification";
import { ContainerUser } from "@/models/ContainerUser";
import _ from "lodash";
import { PositionTemplateFolder } from "@/models/PositionTemplateFolder";
import CustomThemeBuilder from "@/features/shared/models/CustomThemeBuilder";

const client = myContainer.get<IEmploymentService>(TYPES.IEmploymentService);
const themeBuilder = new CustomThemeBuilder();
const getDefaultTheme = (): dtos.CustomTheme => themeBuilder.getDefaultTheme();
export default {
  state: {
    containers: [] as Array<dtos.ContainerResponse>,
    container: {} as dtos.ContainerResponse,
    publicContainer: {} as dtos.ContainerResponse,
    mainContainer: {} as MainContainer,
    searchResult: [] as Array<dtos.ApplicantPositionResponse>,
    searchPending: false,
    containerUsers: [] as Array<ContainerUser>,
    containerPositions: [] as Array<dtos.PositionResponse>,
    containerApplicants: [] as Array<dtos.ApplicantPositionResponse>,
    emailVariables: [] as Array<dtos.EmailVariable>,
    emailTemplates: [] as Array<dtos.EmailTemplate>,
    containerFolders: [] as Array<dtos.FolderResponse>,
    positionTemplateFolder: {} as PositionTemplateFolder,
    customTheme: {} as dtos.CustomTheme,
    textTemplates: [] as Array<dtos.TextTemplate>,
    savedTextTemplates: []
  },

  getters: {
    containers: (state: any) => state.containers,
    searchResult: (state: any) => state.searchResult,
    container: (state: any) => state.container,
    publicContainer: (state: any) => state.publicContainer,
    getLabelInteres(state: any, getters: any) {
      return getters.container?.labels?.filter(
        (px: dtos.LabelResponse) =>
          px.labelType.name.toLowerCase() === "interest"
      );
    },
    getLabelProcesses(state: any, getters: any) {
      return getters.container?.labels?.filter(
        (px: any) => px.labelType.name.toLowerCase() === "process"
      );
    },
    positionTypes: (state: any) =>
      state.container?.positionTypes as dtos.PositionTypeResponse,
    getPositionTemplatesFolder(state: any) {
      return state.positionTemplateFolder;
    },
    getPositionTemplateFolder(state: any) {
      return state.containerFolders?.find(
        (px: dtos.FolderResponse) => px.name === "PositionTemplates"
      );
    },
    mainContainer: (state: any) => state.mainContainer,
    getContainerUsers(state: any, getters: any) {
      return getters.container?.users;
    },
    getContainerTemplates(state: any, getters: any) {
      return getters?.container?.metadata?.properties.find(
        (px: any) => px.key === "system_templates"
      );
    },
    getEmailTemplates: (state: any) => state.emailTemplates,
    getEmailVariables: (state: any) => state.emailVariables,
    getTextTemplates: (state: any) => state.textTemplates,
    getContainerFolders: (state: any) => state.containerFolders,
    getContainerPermissions: (getters: any) => {
      return getters.container?.permissions;
    },
    getContainerTheme: (state: any) => {
      return state.customTheme;
    },
    getContainerDefaultTheme: (state: any) => {
      return getDefaultTheme();
    },
    getContainerThemedText: (state: any) => {
      return state.customTheme?.textColor;
    },
    getContainerThemedButton: (state: any) => {
      return state.customTheme?.button;
    },
    getContainerThemedBackground: (state: any) => {
      return state.customTheme?.backgroundColor;
    },
    getContainerThemedFooterBackground: (state: any) => {
      return state.customTheme?.backgroundColor.footer;
    },
    getContainerThemedIcon: (state: any) => {
      return state.customTheme?.icon;
    },
  },

  mutations: {
    setPositionTemplate(state: any, item: PositionTemplateFolder) {
      state.positionTemplateFolder = item;
    },
    setContainers(state: any, containers: any) {
      state.containers = containers;
    },
    search(state: any, items: any) {
      state.searchResult = items;
    },
    logout(state: any) {
      localStorage.removeItem("containerId");
    },
    setContainer(state: any, container: dtos.ContainerResponse) {
      if (container === null) {
        localStorage.removeItem("vuex");
        return;
      }

      localStorage.setItem("containerId", container.id.toString());
      state.container = container;
      state.mainContainer = new MainContainer(state.container);
    },
    setPublicContainer(state: any, container: dtos.ContainerResponse) {
      if (container === null) return;
      state.publicContainer = container;
    },
    setEmailVariables(state: any, variables: any) {
      state.emailVariables = variables;
    },
    setEmailTemplates(state: any, templates: any) {
      state.emailTemplates = templates;
    },
    setTextTemplates(state: any, templates: any) {
      state.textTemplates = templates;
    },
    setContainerFolders(state: any, folders: any) {
      state.containerFolders = folders;
    },
    setContainerTheme(state: any, theme: dtos.CustomTheme) {
      state.customTheme = theme;
    },
  },

  actions: {
    async loadContainerTheme({ getters, commit, state }: any) {
      try {
        let theme = getters.container.theme;
        if (!theme) {
          theme = getters.publicContainer.theme;
        }
        commit("setContainerTheme", theme);
      } catch (error) {
        console.error("Fel vid laddning av tema:", error);
        // console.log("loadContainerTheme", this.loadContainerTheme)
      }
    },
    async updateContainerTheme(
      { dispatch, getters }: any,
      theme: dtos.CustomTheme
    ) {
      const payload = {
        containerId: getters.container.id,
        theme: theme,
      };
      await client.Container.updateContainerTheme(payload);
      dispatch("loadContainerTheme");
    },
    async resetContainerTheme({ dispatch, getters }: any) {
      await client.Container.resetContainerTheme(getters.container.id);
      dispatch("loadContainerTheme");
    },
    async containerStoreLogout({ commit }: any) {
      commit("logout");
    },
    async deleteContainer({ state, dispatch }: any, id: number) {
      await client.Container.delete(id);
    },

    async init({ state, dispatch }: any) {
      if (state.container.id) {
        return;
      }

      const containerId = localStorage.getItem("containerId");
      await dispatch("loadContainer", parseInt(containerId));
    },
    async updateLabelTemplate(
      { commit, getters, dispatch }: any,
      label: dtos.LabelTemplate
    ) {
      await client.Container.updateContainerTemplate(
        getters.container.id,
        label.labelId,
        label.name,
        label.subject,
        label.content
      );

      dispatch("loadContainer", getters.container.id);
    },
    async updateLabelTemplates(
      { getters, dispatch }: any,
      labels: dtos.LabelTemplate[]
    ) {
      for (let i = 0; i < labels.length; i++) {
        const label = labels[i];
        await client.Container.updateContainerTemplate(
          getters.container.id,
          label.labelId,
          label.name,
          label.subject,
          label.content
        );
      }
      dispatch("loadContainer", getters.container.id);
    },

    async createPositionType({ commit, getters }: any, name: string) {
      const _container = getters.container;
      await client.Container.createPositionType(name, _container.id);
    },
    async loadContainers({ commit, dispatch }: any, id: number) {
      const response = await client.Container.getContainers();

      commit("setContainers", response);
    },
    async loadContainer(
      { commit, dispatch }: any,
      id: number
    ): Promise<dtos.ContainerResponse> {
      const response = await client.Container.getById(id);
      commit("setContainer", response);
      dispatch("loadContainerFolders");

      return new Promise((resolve) => {
        resolve(response);
      });
    },
    async reloadContainer({ getters, dispatch }: any) {
      dispatch("loadContainer", getters.container.id);
    },
    async createContainer({ getters, dispatch }: any, payload: any) {
      return new Promise((resolve) => {
        client.Container.create(payload.name, payload.plans).then(
          async (res) => {
            const _container = await client.Container.getById(res);
            resolve(_container);
          }
        );
      });
    },
    async updateMetadata(
      { getters, dispatch }: any,
      metadata: ContainerResponse
    ) {
      const dto = new dtos.UpdateContainerMetadataRequest(
        getters.container.id,
        metadata.metadata,
        metadata.theme
      );
      await client.Container.updateMetadata(dto);
      dispatch("addNotification", new ToastNotification("success", "Sparad"));
      dispatch("loadContainer", getters.container.id);
    },

    async search({ commit, getters }: any, query: string) {
      const result = await client.Container.search(query, getters.container.id);
      commit("search", result);
    },

    async loadEmailVariables({ commit }: any) {
      const result = await client.Container.getEmailVariables();
      commit("setEmailVariables", result);
    },

    async loadEmailTemplates({ commit, getters }: any) {
      const result = await client.Container.getEmailTemplates(
        getters.container.id
      );
      commit("setEmailTemplates", result);
    },
    async upsertEmailTemplate({ dispatch }: any, template: dtos.EmailTemplate) {
      try {
        await client.Container.upsertEmailTemplate(template);
        dispatch("addNotification", new ToastNotification("success", "Sparad"));
      } catch (e: any) {
        dispatch("addNotification", new ToastNotification("alert", e.message));
        return;
      } finally {
        dispatch("loadEmailTemplates");
      }
    },
    async loadTextTemplates({ commit, getters }: any) {
      const result = await client.Container.getTextTemplates(
        getters.container.id
      );
      commit("setTextTemplates", result);
    },
    async upsertTextTemplate({ dispatch }: any, template: dtos.TextTemplate) {
      try {
        await client.Container.upsertTextTemplate(template);
        dispatch("addNotification", new ToastNotification("success", "Sparad"));
      } catch (e: any) {
        dispatch("addNotification", new ToastNotification("alert", e.message));
        return;
      } finally {
        dispatch("loadTextTemplates");
      }
    },

    async deleteApplicantFromContainer(
      { dispatch }: any,
      payload: dtos.ContainerApplicant
    ) {
      try {
        await client.Container.deleteApplicantFromContainer({
          userId: payload.userId,
          containerId: payload.containerId,
        });
      } catch (e: any) {
        dispatch("addNotification", new ToastNotification("alert", e.message));
        return;
      } finally {
        dispatch("loadContainer", payload.containerId);
        dispatch(
          "addNotification",
          new ToastNotification("success", "Den ansökande har tagits bort")
        );
      }
    },

    async loadContainerFolder(
      { getters }: any,
      folderId: number
    ): Promise<dtos.FolderResponse> {
      const response = await client.Container.getFolder(
        getters.container.id,
        folderId
      );

      return response;
    },
    async loadContainerFolders({ dispatch, getters, commit }: any) {
      const response = await client.Container.getFolders(getters.container.id);
      commit("setContainerFolders", response);
      await dispatch("loadPositionTemplates");
      return response;
    },
    async createContainerFolder({ getters, dispatch }: any, name: string) {
      await client.Container.createFolder(getters.container.id, name);
      dispatch("loadContainerFolders");
    },
    async createContainerAsset(
      { getters, dispatch }: any,
      payload: {
        id: number;
        folderId: number;
        data: string;
        contentType: string;
        name: string;
      }
    ) {
      await client.Container.createContainerAsset(
        payload.id,
        getters.container.id,
        payload.folderId,
        payload.data,
        payload.contentType, // ska trimmas från data:image/png;base64, till image/png
        payload.name
      );
    },
    async getFolders({ getters, commit }: any) {
      getters.container.userId;
      const folders = await client.Container.getFolders(getters.container.id);
    },
    async deleteContainerAsset(
      { getters, dispatch }: any,
      payload: {
        folderId: number;
        id: number;
      }
    ) {
      await client.Container.deleteContainerAsset(
        getters.container.id,
        payload.folderId,
        payload.id
      );
      dispatch("loadContainerFolders");
    },

    async loadPositionTemplates({ dispatch, getters, commit }: any) {
      const id = getters.getPositionTemplateFolder.id;

      const folder = await client.Container.getFolder(getters.container.id, id);
      const model = new PositionTemplateFolder(folder);
      model.Assets.forEach((element) => {
        dispatch("addPositionTemplateState", element);
      });
      commit("setPositionTemplate", model);
    },
    async loadPublicContainerByName({ commit }: any, name: string) {
      const response = await client.getContainerByName(name);
      commit("setPublicContainer", response);
    },
    async createDemoContainer(
      { commit }: any,
      payload: dtos.CreateDemoContainerRequest
    ) {
      const response = await client.Container.createDemoContainer(payload);

      return response;
    },
    async updateEmailTemplates(
      { getters, dispatch }: any,
      templates: dtos.EmailTemplate[]
    ) {
      const containerId = getters.container.id;
      try {
        await Promise.all(
          templates.map(async (template) => {
            const templateToUpdate = { ...template, containerId };
            await client.Container.upsertEmailTemplate(templateToUpdate);
          })
        );
      } catch (e: any) {
        throw new Error(e);
      } finally {
        dispatch("loadEmailTemplates");
      }

      return templates.length > 1
        ? "E-postmallar uppdaterade"
        : "E-postmall uppdaterad";
    },
  },
};
